import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { authFetch } from "../../../services/authFetch";
import config from "../../../config";
import { CitationHoverCard } from "../components/CitationHoverCard";
import { DateCarousel, DayInfo } from "../components/DateCarousel";
import { Download, Clock, Sun, Sunset, Moon, FileText } from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────

type TabKey = "daily" | "shift_morning" | "shift_afternoon" | "shift_night" | "bitacora";

interface HourlyBlock {
  id?: string;
  type: string;
  window_start: string;
  window_end?: string;
  content: string;
  log_count?: number;
  generated_at?: string;
}

interface ShiftData {
  type: string;
  label: string;
  start: string;
  end: string;
  status: "completed" | "in_progress" | "future";
  summary: { content: string; log_count?: number; generated_at?: string } | null;
  hourlyBlocks: HourlyBlock[];
  logbookBlocks?: HourlyBlock[];
}

interface DailySummary {
  content: string;
  log_count?: number;
  generated_at?: string;
}

interface DayData {
  previousShifts: ShiftData[];
  dailySummary: DailySummary | null;
}

interface Props {
  projectId: string;
}

// ── Tab definitions ────────────────────────────────────────────────

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const TABS: TabDef[] = [
  { key: "daily", label: "Día Completo", icon: <FileText size={14} />, color: "#EC4899" },
  { key: "shift_morning", label: "Mañana", icon: <Sun size={14} />, color: "#F59E0B" },
  { key: "shift_afternoon", label: "Tarde", icon: <Sunset size={14} />, color: "#3B82F6" },
  { key: "shift_night", label: "Noche", icon: <Moon size={14} />, color: "#6366F1" },
];

// ── Helpers ────────────────────────────────────────────────────────

/** Convert a window_start ISO string to a YYYY-MM-DD date in MX timezone */
function toMxDateIso(iso: string): string {
  const d = new Date(iso);
  // MX is UTC-6 ; a "day" starts at 07:00 MX = 13:00 UTC
  // If the UTC hour is < 13, the MX day is the previous calendar day
  const utcH = d.getUTCHours();
  const adjusted = new Date(d);
  if (utcH < 13) {
    adjusted.setUTCDate(adjusted.getUTCDate() - 1);
  }
  return adjusted.toISOString().slice(0, 10);
}

function formatMxTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-MX", {
    timeZone: "America/Mexico_City",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMxTimeSeconds(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-MX", {
    timeZone: "America/Mexico_City",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function getTodayMxIso(): string {
  const now = new Date();
  return toMxDateIso(now.toISOString());
}

// ── Component ──────────────────────────────────────────────────────

export const ProjectRollingSummaryView: React.FC<Props> = ({ projectId }) => {
  const { t } = useTranslation();

  // Date carousel state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<TabKey>("daily");

  // Data for the selected day
  const [dayData, setDayData] = useState<DayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Export loading & modal
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportGeneral, setExportGeneral] = useState(true);
  const [exportMorning, setExportMorning] = useState(true);
  const [exportAfternoon, setExportAfternoon] = useState(true);
  const [exportNight, setExportNight] = useState(true);
  const [exportHourly, setExportHourly] = useState(false);
  const [exportGlossary, setExportGlossary] = useState(true);

  // Citation hover state
  const [activeCitation, setActiveCitation] = useState<{
    text: string;
    id: string;
    x: number;
    y: number;
  } | null>(null);

  // ── Close citation on scroll / click ──
  useEffect(() => {
    if (!activeCitation) return;
    const hide = () => setActiveCitation(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("click", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("click", hide);
    };
  }, [activeCitation]);

  // ── Generate recording days (same as ParticipantSummaryDetail) ──
  const recordingDays = useMemo(() => {
    const days: DayInfo[] = [];
    let current = new Date("2026-05-17T12:00:00-06:00");
    const todayMXString = new Date().toLocaleString("en-US", { timeZone: "America/Mexico_City" });
    const todayMXDate = new Date(todayMXString);
    todayMXDate.setHours(23, 59, 59, 999);

    let dayNumber = 1;
    while (current <= todayMXDate) {
      const mxDateString = current
        .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
        .slice(0, 10);
      const todayIsoMX = todayMXDate
        .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
        .slice(0, 10);
      days.push({
        iso: mxDateString,
        date: new Date(current),
        isToday: mxDateString === todayIsoMX,
        dayNumber,
      });
      current.setDate(current.getDate() + 1);
      dayNumber++;
    }
    return days;
  }, []);

  // ── Resolve the actual date ISO for data fetching ──
  const effectiveDate = useMemo(() => {
    if (selectedDate) return selectedDate;
    const todayDay = recordingDays.find((d) => d.isToday);
    if (todayDay) return todayDay.iso;
    if (recordingDays.length > 0) return recordingDays[recordingDays.length - 1].iso;
    return null;
  }, [selectedDate, recordingDays]);

  // ── Fetch day data when date changes ──
  useEffect(() => {
    if (!effectiveDate) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(
          `${config.API_URL}/api/projects/${projectId}/shift-summaries/date/${effectiveDate}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) {
          setDayData({
            previousShifts: body.previousShifts || [],
            dailySummary: body.dailySummary || null,
          });
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || "Error al cargar");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, effectiveDate]);

  // ── Export PDF handler ──
  const handleExportPdf = useCallback(async () => {
    if (!effectiveDate || exporting) return;
    setExporting(true);
    setExportModalOpen(false);
    try {
      const params = new URLSearchParams();
      if (exportGeneral) params.set("general", "true");

      const selectedShifts = [];
      if (exportMorning) selectedShifts.push("morning");
      if (exportAfternoon) selectedShifts.push("afternoon");
      if (exportNight) selectedShifts.push("night");

      if (selectedShifts.length > 0) {
        params.set("shifts", "true");
        params.set("shiftTypes", selectedShifts.join(","));
      }

      if (exportHourly) params.set("hourly", "true");
      params.set("glossary", exportGlossary ? "true" : "false");

      const res = await authFetch(
        `${config.API_URL}/api/projects/${projectId}/shift-summaries/date/${effectiveDate}/export-pdf?${params.toString()}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Resumen-Braindog-${effectiveDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Could show a toast here
    } finally {
      setExporting(false);
    }
  }, [
    projectId,
    effectiveDate,
    exporting,
    exportGeneral,
    exportMorning,
    exportAfternoon,
    exportNight,
    exportHourly,
    exportGlossary,
  ]);

  // ── renderBold: convert **text** to <strong> ──
  const renderBold = (text: string): React.ReactNode => {
    const boldParts = text.split(/\*\*(.*?)\*\*/g);
    if (boldParts.length === 1) return text;
    return boldParts.map((part, i) =>
      i % 2 === 1 ? (
        <strong key={i} style={{ fontWeight: 700 }}>
          {part}
        </strong>
      ) : (
        <span key={i}>{part}</span>
      ),
    );
  };

  // ── renderContent: parse text with citations ──
  const renderContent = useCallback(
    (fullContent: string) => {
      const parts = fullContent.split(/--- GLOSARIO DE CITAS ---/i);
      const mainText = parts[0].trim();
      const glossaryText = parts.length > 1 ? parts[1].trim() : "";

      const glossaryMap = new Map<string, string>();
      const glossaryRegex = /\[((?:LOG|TRANS|OTF)-[^\]]+)\](.*)/g;
      let match;
      while ((match = glossaryRegex.exec(glossaryText)) !== null) {
        glossaryMap.set(match[1], match[2].trim());
      }

      let seqCounter = 1;
      const citationNumMap = new Map<string, number>();

      const lines = mainText.split("\n");
      const elements: React.ReactNode[] = [];

      lines.forEach((line, lineIdx) => {
        const trimmed = line.trim();
        if (!trimmed) {
          elements.push(<br key={`br-${lineIdx}`} />);
          return;
        }

        // Turno header: --- Turno de Mañana (07:00 - 15:00) ---
        const turnoMatch = trimmed.match(/^-{2,}\s*(Turno\s+de\s+.+?)\s*-{2,}$/i);
        if (turnoMatch) {
          elements.push(
            <div
              key={`turno-${lineIdx}`}
              style={{
                background: "linear-gradient(135deg, #EC4899, #DB2777)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem",
                padding: "8px 14px",
                borderRadius: "6px",
                marginTop: "16px",
                marginBottom: "8px",
                letterSpacing: "0.5px",
              }}
            >
              {turnoMatch[1]}
            </div>,
          );
          return;
        }

        // Block header: --- Bloque 07:00 - 08:00 ---
        const bloqueMatch = trimmed.match(/^-{2,}\s*(Bloque\s+.+?)\s*-{2,}$/i);
        if (bloqueMatch) {
          elements.push(
            <div
              key={`bloque-${lineIdx}`}
              style={{
                background: "rgba(255,255,255,0.06)",
                color: "#EC4899",
                fontWeight: 700,
                fontSize: "0.75rem",
                padding: "5px 12px",
                borderRadius: "4px",
                marginTop: "12px",
                marginBottom: "6px",
                borderLeft: "3px solid #EC4899",
              }}
            >
              {bloqueMatch[1]}
            </div>,
          );
          return;
        }

        // Markdown heading: ### Heading Text
        const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
          const level = headingMatch[1].length; // 1, 2, or 3
          const sizes = { 1: "1rem", 2: "0.9rem", 3: "0.85rem" } as Record<number, string>;
          elements.push(
            <div
              key={`h-${lineIdx}`}
              style={{
                color: "#EC4899",
                fontWeight: 700,
                fontSize: sizes[level] || "0.85rem",
                marginTop: level === 1 ? "20px" : "14px",
                marginBottom: "6px",
                borderBottom: level <= 2 ? "1px solid rgba(236,72,153,0.3)" : "none",
                paddingBottom: level <= 2 ? "4px" : "0",
              }}
            >
              {renderBold(headingMatch[2])}
            </div>,
          );
          return;
        }

        // Section header: ALL CAPS text ending with colon (e.g. "REALITY DESPERTAR:", "FIN DEL CASTIGO (ESPOSAS):")
        const sectionMatch = trimmed.match(/^([A-ZÁÉÍÓÚÑ\s()]+):$/);
        if (sectionMatch && trimmed.length < 60) {
          elements.push(
            <div
              key={`section-${lineIdx}`}
              style={{
                color: "#EC4899",
                fontWeight: 700,
                fontSize: "0.82rem",
                marginTop: "12px",
                marginBottom: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              {sectionMatch[1]}:
            </div>,
          );
          return;
        }

        const inlinePattern = /\[((?:LOG|TRANS|OTF)-[^\]]+)\]/g;
        const inlineParts: React.ReactNode[] = [];
        let lastIdx = 0;
        let inlineMatch;

        while ((inlineMatch = inlinePattern.exec(line)) !== null) {
          if (inlineMatch.index > lastIdx) {
            inlineParts.push(
              <span key={`t-${lineIdx}-${lastIdx}`}>
                {renderBold(line.slice(lastIdx, inlineMatch.index))}
              </span>,
            );
          }

          const citKey = inlineMatch[1];
          if (!citationNumMap.has(citKey)) {
            citationNumMap.set(citKey, seqCounter++);
          }
          const seqNum = citationNumMap.get(citKey);
          const citText = glossaryMap.get(citKey) || "Fuente original de log";

          const isOtf = citKey.startsWith("OTF-");
          const isTrans = citKey.startsWith("TRANS-");
          const bgColor = isOtf
            ? "var(--color-data-otf, #06b6d4)"
            : isTrans
              ? "#8B5CF6"
              : "var(--color-data-log, #EC4899)";

          inlineParts.push(
            <sup
              key={`cit-${lineIdx}-${inlineMatch.index}`}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setActiveCitation({ text: citText, id: citKey, x: rect.left, y: rect.top - 10 });
              }}
              onMouseLeave={() => setActiveCitation(null)}
              style={{
                color: "#fff",
                background: bgColor,
                borderRadius: "3px",
                padding: "1px 5px",
                fontSize: "0.65rem",
                marginLeft: "4px",
                cursor: "pointer",
                userSelect: "none",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
            >
              {seqNum}
            </sup>,
          );
          lastIdx = inlinePattern.lastIndex;
        }

        if (lastIdx < line.length) {
          inlineParts.push(<span key={`t-end-${lineIdx}`}>{renderBold(line.slice(lastIdx))}</span>);
        }

        elements.push(
          <div key={`line-${lineIdx}`} style={{ marginBottom: "0.5em" }}>
            {inlineParts}
          </div>,
        );
      });

      return elements;
    },
    [], // setActiveCitation is stable
  );

  // ── Get shift data by type ──
  const getShift = useCallback(
    (type: string): ShiftData | undefined => {
      return dayData?.previousShifts.find((s) => s.type === type);
    },
    [dayData],
  );

  // ── Collect all logbook blocks for Bitacora tab ──
  const allLogbookBlocks = useMemo(() => {
    if (!dayData) return [];
    const blocks: HourlyBlock[] = [];
    for (const shift of dayData.previousShifts) {
      if (shift.logbookBlocks) {
        blocks.push(...shift.logbookBlocks);
      } else if (shift.hourlyBlocks) {
        // Fallback: use hourly blocks if logbook not separate
        blocks.push(...shift.hourlyBlocks);
      }
    }
    // Sort by window_start
    blocks.sort((a, b) => new Date(a.window_start).getTime() - new Date(b.window_start).getTime());
    return blocks;
  }, [dayData]);

  // ── Render shift content (Mañana, Tarde, Noche) ──
  const renderShiftContent = useCallback(
    (shiftType: string) => {
      const shift = getShift(shiftType);
      if (!shift) {
        return (
          <div style={emptyStateStyle}>
            <Clock size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>No hay datos para este turno</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.5, marginTop: 4 }}>
              Los resumenes se generan al finalizar cada turno
            </div>
          </div>
        );
      }

      if (shift.status === "future") {
        return (
          <div style={emptyStateStyle}>
            <Clock size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>Turno pendiente</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.5, marginTop: 4 }}>
              {formatMxTime(shift.start)} - {formatMxTime(shift.end)}
            </div>
          </div>
        );
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Shift summary */}
          {shift.summary && shift.summary.content && (
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                  Resumen - Turno de {shift.label}
                </div>
                <div style={metaStyle}>
                  {formatMxTime(shift.start)} - {formatMxTime(shift.end)}
                  {shift.summary.log_count != null && <> &middot; {shift.summary.log_count} logs</>}
                </div>
              </div>
              <div style={cardBodyStyle}>{renderContent(shift.summary.content)}</div>
            </div>
          )}

          {/* Hourly blocks for this shift */}
          {shift.hourlyBlocks && shift.hourlyBlocks.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {shift.hourlyBlocks
                .filter((b) => b.type === "hourly")
                .map((block, idx) => (
                  <div key={block.id || idx} style={cardStyle}>
                    <div style={cardHeaderStyle}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            background: "rgba(236,72,153,0.15)",
                            color: "#EC4899",
                            padding: "2px 10px",
                            borderRadius: "6px",
                            fontSize: "0.7rem",
                            fontWeight: 800,
                          }}
                        >
                          {formatMxTime(block.window_start)}
                          {block.window_end ? ` - ${formatMxTime(block.window_end)}` : ""}
                        </span>
                        {block.log_count != null && (
                          <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>
                            {block.log_count} logs
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={cardBodyStyle}>{renderContent(block.content)}</div>
                  </div>
                ))}
            </div>
          )}

          {/* In-progress state */}
          {shift.status === "in_progress" && !shift.summary && shift.hourlyBlocks.length === 0 && (
            <div style={emptyStateStyle}>
              <div style={pulseStyle} />
              <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>Turno en progreso</div>
              <div style={{ fontSize: "0.7rem", opacity: 0.5, marginTop: 4 }}>
                {formatMxTime(shift.start)} - {formatMxTime(shift.end)}
              </div>
            </div>
          )}
        </div>
      );
    },
    [getShift, renderContent],
  );

  // ── Render daily (Día Completo) ──
  const renderDailyContent = useCallback(() => {
    if (!dayData) return null;

    const hasContent =
      dayData.dailySummary ||
      dayData.previousShifts.some(
        (s) => (s.summary && s.summary.content) || (s.hourlyBlocks && s.hourlyBlocks.length > 0),
      );

    if (!hasContent) {
      return (
        <div style={emptyStateStyle}>
          <Clock size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>No hay resumen para este dia</div>
          <div style={{ fontSize: "0.7rem", opacity: 0.5, marginTop: 4 }}>
            Los resumenes diarios se generan al final de la jornada
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Daily narrative summary if available */}
        {dayData.dailySummary && dayData.dailySummary.content && (
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>Resumen del día</div>
              {dayData.dailySummary.generated_at && (
                <div style={metaStyle}>
                  Generado: {formatMxTimeSeconds(dayData.dailySummary.generated_at)}
                  {dayData.dailySummary.log_count != null && (
                    <> &middot; {dayData.dailySummary.log_count} logs</>
                  )}
                </div>
              )}
            </div>
            <div style={cardBodyStyle}>{renderContent(dayData.dailySummary.content)}</div>
          </div>
        )}

        {/* Per-shift breakdown */}
        {dayData.previousShifts
          .filter((s) => s.status !== "future")
          .map((shift) => {
            const hasShiftContent =
              (shift.summary && shift.summary.content) ||
              (shift.hourlyBlocks &&
                shift.hourlyBlocks.filter((b) => b.type === "hourly").length > 0);
            if (!hasShiftContent) return null;

            return (
              <div key={shift.type}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "2px",
                    color: "#EC4899",
                    textTransform: "uppercase",
                    marginBottom: 12,
                    paddingBottom: 6,
                    borderBottom: "1px solid rgba(236,72,153,0.2)",
                  }}
                >
                  Turno de {shift.label}
                </div>

                {shift.summary && shift.summary.content && (
                  <div style={{ ...cardStyle, marginBottom: 12 }}>
                    <div style={cardBodyStyle}>{renderContent(shift.summary.content)}</div>
                  </div>
                )}

                {shift.hourlyBlocks &&
                  shift.hourlyBlocks
                    .filter((b) => b.type === "hourly")
                    .map((block, idx) => (
                      <div key={block.id || idx} style={{ ...cardStyle, marginBottom: 8 }}>
                        <div style={cardHeaderStyle}>
                          <span
                            style={{
                              background: "rgba(236,72,153,0.12)",
                              color: "#EC4899",
                              padding: "2px 10px",
                              borderRadius: "6px",
                              fontSize: "0.7rem",
                              fontWeight: 800,
                            }}
                          >
                            {formatMxTime(block.window_start)}
                            {block.window_end ? ` - ${formatMxTime(block.window_end)}` : ""}
                          </span>
                        </div>
                        <div style={cardBodyStyle}>{renderContent(block.content)}</div>
                      </div>
                    ))}
              </div>
            );
          })}
      </div>
    );
  }, [dayData, renderContent]);

  // ── Render Bitacora ──
  const renderBitacora = useCallback(() => {
    if (allLogbookBlocks.length === 0) {
      return (
        <div style={emptyStateStyle}>
          <Clock size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>Bitácora no disponible</div>
          <div style={{ fontSize: "0.7rem", opacity: 0.5, marginTop: 4 }}>
            Los bloques horarios se generan automaticamente
          </div>
        </div>
      );
    }

    return (
      <div>
        <div
          style={{
            fontSize: "0.75rem",
            fontWeight: 800,
            letterSpacing: "1.5px",
            color: "var(--color-text-muted)",
            textTransform: "uppercase",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Clock size={14} />
          BITACORA (JORNADA DETALLADA)
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {allLogbookBlocks.map((block, blockIdx) => {
            const startTime = formatMxTime(block.window_start);
            const endTime = block.window_end ? formatMxTime(block.window_end) : "";

            // Parse the block content into individual timeline entries
            const entries = parseLogbookEntries(block.content);

            return (
              <div key={block.id || blockIdx} style={{ position: "relative" }}>
                {/* Hour header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                    marginBottom: 8,
                    marginTop: blockIdx > 0 ? 20 : 0,
                  }}
                >
                  <div style={{ minWidth: 100, textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: "0.85rem", color: "#fff" }}>
                      {startTime}
                    </div>
                    {endTime && (
                      <div
                        style={{
                          fontSize: "0.65rem",
                          color: "var(--color-text-muted)",
                          marginTop: 1,
                        }}
                      >
                        {endTime}
                      </div>
                    )}
                    {block.log_count != null && (
                      <div
                        style={{
                          fontSize: "0.6rem",
                          color: "#EC4899",
                          marginTop: 2,
                          fontStyle: "italic",
                        }}
                      >
                        {block.log_count} logs
                      </div>
                    )}
                  </div>

                  {/* Timeline dot */}
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#EC4899",
                      boxShadow: "0 0 8px rgba(236,72,153,0.6)",
                      flexShrink: 0,
                      marginTop: 4,
                    }}
                  />

                  {/* Timeline line */}
                  <div
                    style={{
                      position: "absolute",
                      left: 121,
                      top: 22,
                      bottom: 0,
                      width: 2,
                      background:
                        blockIdx < allLogbookBlocks.length - 1
                          ? "rgba(236,72,153,0.15)"
                          : "transparent",
                    }}
                  />
                </div>

                {/* Entries */}
                <div style={{ marginLeft: 128, paddingBottom: 4 }}>
                  {entries.length > 0 ? (
                    entries.map((entry, entryIdx) => (
                      <div
                        key={entryIdx}
                        style={{
                          display: "flex",
                          gap: 10,
                          marginBottom: 6,
                          lineHeight: 1.5,
                          fontSize: "0.8rem",
                        }}
                      >
                        {entry.time && (
                          <span
                            style={{
                              color: "var(--color-text-muted)",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              flexShrink: 0,
                              minWidth: 42,
                            }}
                          >
                            {entry.time}
                          </span>
                        )}
                        <span style={{ color: "var(--color-text-primary)" }}>
                          {renderContent(entry.text)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{ lineHeight: 1.6, fontSize: "0.8rem" }}>
                      {renderContent(block.content)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [allLogbookBlocks, renderContent]);

  // ── Tab content switcher ──
  const renderTabContent = useCallback(() => {
    if (loading) {
      return (
        <div style={{ padding: 40, color: "var(--color-text-muted)", textAlign: "center" }}>
          <div style={spinnerStyle} />
          Cargando...
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: 40, color: "var(--color-accent-danger)", textAlign: "center" }}>
          {error}
        </div>
      );
    }

    if (!dayData) {
      return (
        <div style={emptyStateStyle}>
          <Clock size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>Selecciona un dia</div>
        </div>
      );
    }

    switch (activeTab) {
      case "daily":
        return renderDailyContent();
      case "shift_morning":
      case "shift_afternoon":
      case "shift_night":
        return renderShiftContent(activeTab);
      case "bitacora":
        return renderBitacora();
      default:
        return null;
    }
  }, [loading, error, dayData, activeTab, renderDailyContent, renderShiftContent, renderBitacora]);

  // ── Render ──
  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* Date Carousel */}
      {recordingDays.length > 0 && (
        <DateCarousel
          recordingDays={recordingDays}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      )}

      {/* Toolbar: Tabs + Export */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {/* Tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 0, flexWrap: "wrap" }}>
          {/* Main 4 tabs */}
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  background: isActive ? tab.color : "transparent",
                  color: isActive ? "#fff" : "var(--color-text-muted)",
                  border: "none",
                  padding: "10px 22px",
                  cursor: "pointer",
                  fontSize: "0.78rem",
                  fontWeight: isActive ? 700 : 500,
                  letterSpacing: "0.3px",
                  transition: "all 0.2s ease",
                  fontFamily: "inherit",
                  borderRadius: isActive ? "8px" : "0",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = tab.color;
                    e.currentTarget.style.background = `${tab.color}15`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = "var(--color-text-muted)";
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}

          {/* Separator */}
          <div
            style={{
              width: 1,
              height: 24,
              background: "rgba(255,255,255,0.1)",
              margin: "0 12px",
            }}
          />

          {/* Bitacora tab — distinct style */}
          <button
            onClick={() => setActiveTab("bitacora")}
            style={{
              background: activeTab === "bitacora" ? "rgba(236,72,153,0.15)" : "transparent",
              color: activeTab === "bitacora" ? "#EC4899" : "var(--color-text-muted)",
              border: `1px solid ${activeTab === "bitacora" ? "#EC4899" : "rgba(255,255,255,0.12)"}`,
              padding: "8px 20px",
              cursor: "pointer",
              fontSize: "0.78rem",
              fontWeight: activeTab === "bitacora" ? 700 : 500,
              letterSpacing: "0.3px",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              borderRadius: "9999px",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== "bitacora") {
                e.currentTarget.style.borderColor = "rgba(236,72,153,0.4)";
                e.currentTarget.style.color = "#EC4899";
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== "bitacora") {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                e.currentTarget.style.color = "var(--color-text-muted)";
              }
            }}
          >
            <Clock size={14} />
            Bitácora
          </button>
        </div>

        {/* Export button */}
        <button
          onClick={() => setExportModalOpen(true)}
          disabled={exporting || !effectiveDate}
          style={{
            background: "transparent",
            color: "#EC4899",
            border: "1px solid #EC4899",
            padding: "8px 18px",
            borderRadius: "9999px",
            cursor: exporting ? "wait" : "pointer",
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.3px",
            transition: "all 0.2s ease",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            gap: 6,
            opacity: exporting ? 0.5 : 1,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            if (!exporting) e.currentTarget.style.background = "rgba(236,72,153,0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <Download size={14} />
          {exporting ? "Exportando..." : "Exportar PDF"}
        </button>
      </div>

      {/* Content area */}
      <div style={{ minHeight: 200 }}>{renderTabContent()}</div>

      {/* Citation hover card */}
      {activeCitation && <CitationHoverCard citation={activeCitation} />}

      {/* Export Modal */}
      {exportModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setExportModalOpen(false);
          }}
        >
          <div
            style={{
              background: "#1f1f1f",
              border: "1px solid #333",
              borderRadius: "12px",
              boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
              width: "100%",
              maxWidth: 440,
              padding: "28px",
              position: "relative",
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setExportModalOpen(false)}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "transparent",
                border: "none",
                color: "#888",
                cursor: "pointer",
                fontSize: "1.2rem",
                fontFamily: "inherit",
                padding: 4,
              }}
            >
              ✕
            </button>

            <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: 8 }}>
              Exportar Resumen del Proyecto
            </h2>
            <p style={{ fontSize: "0.8rem", color: "#999", marginBottom: 20 }}>
              Selecciona las secciones a incluir en el PDF para la fecha seleccionada.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Resumen General */}
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={exportGeneral}
                  onChange={(e) => setExportGeneral(e.target.checked)}
                  style={{ accentColor: "#EC4899", marginTop: 3, width: 16, height: 16 }}
                />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    <FileText size={14} style={{ color: "#EC4899" }} />
                    Resumen General
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                    Resumen narrativo del día completo
                  </div>
                </div>
              </label>

              {/* Turno Mañana */}
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={exportMorning}
                  onChange={(e) => setExportMorning(e.target.checked)}
                  style={{ accentColor: "#F59E0B", marginTop: 3, width: 16, height: 16 }}
                />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    <Sun size={14} style={{ color: "#F59E0B" }} />
                    Turno Mañana
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                    07:00 - 15:00
                  </div>
                </div>
              </label>

              {/* Turno Tarde */}
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={exportAfternoon}
                  onChange={(e) => setExportAfternoon(e.target.checked)}
                  style={{ accentColor: "#3B82F6", marginTop: 3, width: 16, height: 16 }}
                />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    <Sunset size={14} style={{ color: "#3B82F6" }} />
                    Turno Tarde
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                    15:00 - 23:00
                  </div>
                </div>
              </label>

              {/* Turno Noche */}
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={exportNight}
                  onChange={(e) => setExportNight(e.target.checked)}
                  style={{ accentColor: "#6366F1", marginTop: 3, width: 16, height: 16 }}
                />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    <Moon size={14} style={{ color: "#6366F1" }} />
                    Turno Noche
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                    23:00 - 07:00
                  </div>
                </div>
              </label>

              {/* Bitácora */}
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={exportHourly}
                  onChange={(e) => setExportHourly(e.target.checked)}
                  style={{ accentColor: "#3B82F6", marginTop: 3, width: 16, height: 16 }}
                />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    <Clock size={14} style={{ color: "#3B82F6" }} />
                    Bitácora (Jornada Detallada)
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                    Timeline cronológico con todas las entradas hora a hora
                  </div>
                </div>
              </label>

              {/* Glosario */}
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={exportGlossary}
                  onChange={(e) => setExportGlossary(e.target.checked)}
                  style={{ accentColor: "#8B5CF6", marginTop: 3, width: 16, height: 16 }}
                />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    Incluir Glosario de Citas
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                    Añade un anexo con las transcripciones y logs vinculados a cada cita
                  </div>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setExportModalOpen(false)}
                style={{
                  background: "transparent",
                  color: "#999",
                  border: "1px solid #444",
                  padding: "8px 18px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.78rem",
                  fontFamily: "inherit",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExportPdf}
                disabled={
                  exporting ||
                  (!exportGeneral &&
                    !exportMorning &&
                    !exportAfternoon &&
                    !exportNight &&
                    !exportHourly)
                }
                style={{
                  background: "#EC4899",
                  color: "#fff",
                  border: "none",
                  padding: "8px 22px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  fontFamily: "inherit",
                  opacity:
                    !exportGeneral &&
                    !exportMorning &&
                    !exportAfternoon &&
                    !exportNight &&
                    !exportHourly
                      ? 0.4
                      : 1,
                }}
              >
                {exporting ? "Generando..." : "Descargar PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Logbook entry parser ──

interface LogbookEntry {
  time: string | null;
  text: string;
}

function parseLogbookEntries(content: string): LogbookEntry[] {
  // Split glossary out
  const parts = content.split(/--- GLOSARIO DE CITAS ---/i);
  const mainText = parts[0].trim();
  const glossary = parts.length > 1 ? "\n--- GLOSARIO DE CITAS ---\n" + parts[1] : "";

  const lines = mainText.split("\n").filter((l) => l.trim());
  const entries: LogbookEntry[] = [];

  // Try to detect "HH:MM - text" or "HH:MM:SS - text" pattern
  const timePattern = /^(\d{1,2}:\d{2}(?::\d{2})?)\s*[-–]\s*(.+)/;

  for (const line of lines) {
    const m = timePattern.exec(line.trim());
    if (m) {
      entries.push({ time: m[1], text: m[2] + glossary });
    } else if (entries.length > 0) {
      // Continuation of previous entry
      entries[entries.length - 1].text += "\n" + line.trim();
    } else {
      entries.push({ time: null, text: line.trim() + glossary });
    }
  }

  // Only return parsed entries if we found time patterns; otherwise let caller render raw
  const hasTimedEntries = entries.some((e) => e.time !== null);
  return hasTimedEntries ? entries : [];
}

// ── Styles ──

const cardStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid var(--color-panel-border, rgba(255,255,255,0.08))",
  borderRadius: "8px",
  overflow: "hidden",
};

const cardHeaderStyle: React.CSSProperties = {
  padding: "12px 16px",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 8,
};

const cardBodyStyle: React.CSSProperties = {
  padding: "14px 16px",
  lineHeight: 1.6,
  fontSize: "0.82rem",
  color: "var(--color-text-primary)",
};

const metaStyle: React.CSSProperties = {
  fontSize: "0.65rem",
  color: "var(--color-text-muted)",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "60px 40px",
  color: "var(--color-text-muted)",
  textAlign: "center",
  background: "rgba(255,255,255,0.02)",
  borderRadius: "8px",
  border: "1px dashed rgba(255,255,255,0.1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const spinnerStyle: React.CSSProperties = {
  display: "inline-block",
  width: 20,
  height: 20,
  border: "2px solid rgba(236,72,153,0.3)",
  borderTopColor: "#EC4899",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
  marginRight: 10,
  verticalAlign: "middle",
};

const pulseStyle: React.CSSProperties = {
  width: 12,
  height: 12,
  borderRadius: "50%",
  background: "#EC4899",
  marginBottom: 12,
  animation: "pulse 2s ease-in-out infinite",
};

export default ProjectRollingSummaryView;
